import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CustomerAuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestBookingCount, setGuestBookingCount] = useState(0);
  const [guestPromptDismissed, setGuestPromptDismissed] = useState(
    () => sessionStorage.getItem("mahra_guest_prompt_dismissed") === "true"
  );

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem("mahra_session_token");
      if (!token) { setLoading(false); return; }
      const res = await fetch(`${API}/api/customer/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCustomer(data);
      } else {
        localStorage.removeItem("mahra_session_token");
        setCustomer(null);
      }
    } catch {
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Microsoft Clarity — tag user type (admin / customer / guest)
  useEffect(() => {
    if (loading) return;
    if (typeof window === "undefined" || !window.clarity) return;
    if (localStorage.getItem("adminToken")) {
      window.clarity("set", "user_type", "admin");
    } else if (customer) {
      window.clarity("set", "user_type", "customer");
      window.clarity("set", "customer_name", customer.name || customer.email || "");
    } else {
      window.clarity("set", "user_type", "guest");
    }
  }, [customer, loading]);

  // Check for unlinked guest bookings after login
  useEffect(() => {
    if (!customer) return;
    const token = localStorage.getItem("mahra_session_token");
    if (!token) return;
    fetch(`${API}/api/customer/guest-bookings/check`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { count: 0 })
      .then(d => setGuestBookingCount(d.count))
      .catch(() => {});
  }, [customer]);

  const dismissGuestPrompt = () => {
    setGuestPromptDismissed(true);
    sessionStorage.setItem("mahra_guest_prompt_dismissed", "true");
  };

  const importAllGuestBookings = async () => {
    const token = localStorage.getItem("mahra_session_token");
    const res = await fetch(`${API}/api/customer/guest-bookings/import-all`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      setGuestBookingCount(0);
      dismissGuestPrompt();
      return data.imported;
    }
    return 0;
  };

  const login = (customerData, token) => {
    localStorage.setItem("mahra_session_token", token);
    setCustomer(customerData);
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("mahra_session_token");
      await fetch(`${API}/api/customer/auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
    localStorage.removeItem("mahra_session_token");
    setCustomer(null);
  };

  const refreshCustomer = async () => {
    try {
      const token = localStorage.getItem("mahra_session_token");
      if (!token) return;
      const res = await fetch(`${API}/api/customer/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCustomer(await res.json());
    } catch {}
  };

  return (
    <CustomerAuthContext.Provider value={{ customer, loading, login, logout, refreshCustomer, guestBookingCount, guestPromptDismissed, dismissGuestPrompt, importAllGuestBookings }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  return useContext(CustomerAuthContext);
}
